import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

/**
 * Public account-deletion landing page.
 *
 * **This URL is what gets pasted into Google Play Console → App content →
 * Data deletion** to satisfy Play's data-deletion-disclosure requirement.
 * It is intentionally **no-login** so users (and reviewers) can find the
 * deletion instructions without holding valid credentials.
 *
 * Mirrors agent-os/specs/2026-05-29-account-deletion/store-copy.md § A.
 * If that copy is updated (e.g. legal swaps in the confirmed retention
 * basis/period), update this template in lockstep.
 *
 * Authenticated users get a CTA to /profile/delete-account (the functional
 * mirror of the mobile delete-account screen — Artifact B). Users without
 * access to the app are pointed at the support mailbox as a fallback.
 *
 * Route: `/account-deletion` (top-level, OUTSIDE LayoutComponent, no guard).
 * Production URL: https://app.americandronenetwork.com/account-deletion
 */
@Component({
  selector: 'app-account-deletion-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './account-deletion-page.component.html',
  styleUrls: ['./account-deletion-page.component.scss']
})
export class AccountDeletionPageComponent {
  // Pulled to constants so they're trivial to swap before publishing.
  // Both must be confirmed with legal/support before this page goes live;
  // until then the template surfaces them in plain text exactly as drafted
  // in store-copy.md § A.
  readonly supportEmail = 'support@americandronenetwork.com';
  readonly retentionBasis =
    'as required by the Federal Aviation Administration (FAA) for agricultural drone operations';
  readonly retentionPeriod =
    'the retention period mandated by 14 CFR § 137 and related FAA recordkeeping requirements';

  /** Pre-built mailto link so the CTA can be a plain anchor. */
  get mailtoLink(): string {
    const subject = encodeURIComponent('Account deletion request');
    const body = encodeURIComponent(
      'Please delete the account associated with this email address.\n\n' +
      '(Sent from the American Drone Network account-deletion page.)'
    );
    return `mailto:${this.supportEmail}?subject=${subject}&body=${body}`;
  }
}
